import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Mail, Send, Users, TrendingUp, UserPlus,
  Loader2, CheckCircle, Clock, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const segments = [
  { id: 'all', label: 'All Affiliates', desc: 'Every partner in the program', icon: Users },
  { id: 'active', label: 'Active Partners', desc: 'Partners with active status', icon: CheckCircle },
  { id: 'top_earners', label: 'Top Earners', desc: 'Partners who have earned commissions', icon: TrendingUp },
  { id: 'new_partners', label: 'New Partners (30d)', desc: 'Partners who joined in the last 30 days', icon: UserPlus },
];

export default function AdminAffiliateEmails() {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [segment, setSegment] = useState('all');
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignPage, setCampaignPage] = useState(1);
  const [showCompose, setShowCompose] = useState(false);

  const headers = useCallback(() => {
    const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  }, []);

  const loadCampaigns = useCallback(async (p = 1) => {
    try {
      const res = await fetch(`${API}/affiliate/admin/affiliate-email/campaigns?page=${p}`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        setCampaigns(d.campaigns || []);
        setCampaignsTotal(d.total || 0);
        setCampaignPage(d.page || 1);
      }
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const sendCampaign = async () => {
    if (!subject.trim() || !content.trim()) { toast.error('Subject and content are required'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/affiliate/admin/affiliate-email`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ subject, content, segment }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message);
        setSubject('');
        setContent('');
        setShowCompose(false);
        loadCampaigns();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send');
      }
    } catch { toast.error('Error sending campaign'); }
    setSending(false);
  };

  const totalPages = Math.ceil(campaignsTotal / 20);

  return (
    <div className="max-w-5xl mx-auto" data-testid="affiliate-emails-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
          <div>
            <h1 className="text-xl font-bold text-[#0f1f33]">Affiliate Email Campaigns</h1>
            <p className="text-sm text-gray-400">Send targeted emails to affiliate segments.</p>
          </div>
        </div>
        <Button onClick={() => setShowCompose(!showCompose)} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="compose-campaign-btn">
          <Mail className="w-4 h-4 mr-2" /> {showCompose ? 'Cancel' : 'New Campaign'}
        </Button>
      </div>

      {/* Compose Form */}
      {showCompose && (
        <Card className="border-blue-200 shadow-sm mb-8 bg-blue-50/20">
          <CardContent className="p-6 space-y-5">
            <h3 className="font-semibold text-[#0f1f33]">Compose Campaign</h3>

            {/* Segment Selection */}
            <div>
              <label className="text-sm font-medium text-[#0f1f33] mb-2 block">Target Segment</label>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                {segments.map(s => (
                  <label key={s.id}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border transition-all ${segment === s.id ? 'border-[#2563eb] bg-blue-50' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                    <input type="radio" name="segment" checked={segment === s.id} onChange={() => setSegment(s.id)} className="sr-only" />
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${segment === s.id ? 'bg-[#2563eb] text-white' : 'bg-gray-50 text-gray-400'}`}>
                      <s.icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0f1f33]">{s.label}</p>
                      <p className="text-xs text-gray-400">{s.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Subject</label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g., March Partner Update" data-testid="campaign-subject" />
            </div>

            {/* Content */}
            <div>
              <label className="text-sm font-medium text-[#0f1f33] mb-1.5 block">Email Content</label>
              <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Write your email content here..." className="min-h-[150px]" data-testid="campaign-content" />
            </div>

            {/* Send Button */}
            <div className="flex justify-end">
              <Button onClick={sendCampaign} disabled={sending || !subject.trim() || !content.trim()} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-8" data-testid="send-campaign-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Campaign
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Campaign History */}
      <Card className="border-gray-100 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold text-[#0f1f33] mb-4">Campaign History</h3>
          {campaigns.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">No campaigns sent yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl" data-testid={`campaign-${c.campaign_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-[#0f1f33] truncate">{c.subject}</p>
                      <Badge className="bg-blue-50 text-blue-700 text-xs capitalize">{c.segment}</Badge>
                    </div>
                    <p className="text-xs text-gray-400 truncate">{c.content?.slice(0, 100)}...</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="text-sm font-semibold text-[#0f1f33]">{c.total_recipients} recipients</p>
                    <p className="text-xs text-gray-400">{c.created_at?.slice(0, 10)}</p>
                  </div>
                </div>
              ))}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-4 border-t border-gray-100">
                  <Button variant="ghost" size="sm" disabled={campaignPage <= 1} onClick={() => loadCampaigns(campaignPage - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-sm text-gray-500">Page {campaignPage} of {totalPages}</span>
                  <Button variant="ghost" size="sm" disabled={campaignPage >= totalPages} onClick={() => loadCampaigns(campaignPage + 1)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
